import { getCookie, setCookie, removeCookie } from 'helpers/VanillaCookie';
import { trackAction } from 'helpers/trackingOM';
import { getLocale } from 'services/LocalizationService';

/**
 * ConcurrencyManager
 *
 * This manager is activated by SmartTagAdaptor to check for concurrency limits for all video playback scenarios
 * Based on app configs (enabled, delay time), and the SmartTag 'video playing' event, this manager
 * - sets a timer
 * - calls AJAX endpoint for the concurrency check API
 * - and if the user is over the concurrency threshold
 *   - stops video playback
 *   - displays a concurrency block slate
 *   - configures Redfast upsell pop (INTL use case)
 *   - emits tracking
 */
export default class ConcurrencyManager {
    // enable debug for console output and shorter delay
    static debug = false;
    static debugName = 'debugConcurrency';
    static debugNameForce = 'debugConcurrencyForce';
    static debugDelay = 10;
    static force = false;

    // general configuration
    static enabled;
    static contentId;
    static delay;
    static smartTag;
    static smartTagEvent = 'contentplaying';
    static timerStarted = false;
    static timerContentId = '';
    static eligibleForUpsell = false;
    static stopped = false;

    /**
     * Main entry point to initiate a concurrency check: called from SmartTagAdaptor
     *
     * @param contentId string
     * @param smartTag object
     * @return void
     */
    static concurrencyCheck = (contentId, smartTag) => {
        ConcurrencyManager.contentId = contentId;
        ConcurrencyManager.smartTag = smartTag;
        ConcurrencyManager.configure();
    };

    /**
     * Configure the manager: app configs (feature flags) and debugging
     */
    static configure() {
        // app config values
        ConcurrencyManager.enabled =
            CBS.Registry.Concurrency.concurrency_enforcement_enabled ?? true;
        ConcurrencyManager.delay = CBS.Registry.Concurrency.concurrency_enforcement_delay ?? 30;
        // reset from previous calls (e.g. SPA mode channel changes)
        ConcurrencyManager.timerStarted = false;
        ConcurrencyManager.stopped = false;
        // setup debug
        ConcurrencyManager.configureDebug();
        ConcurrencyManager.debugLog('enabled: ' + ConcurrencyManager.enabled);
        if (ConcurrencyManager.enabled) {
            ConcurrencyManager.debugLog('delay: ' + ConcurrencyManager.delay + ' seconds');
            ConcurrencyManager.debugLog('contentId: ', ConcurrencyManager.contentId);
            ConcurrencyManager.debugLog('force: ' + ConcurrencyManager.force);
        } else {
            ConcurrencyManager.debugLog('disabled');
        }
    }

    /**
     * Check if current video has been stopped due to concurrency block
     * Called from SmartTag on end card or continuous playback
     *
     * @return boolean
     */
    static isStopped() {
        ConcurrencyManager.debugLog('isStopped: ', ConcurrencyManager.stopped);
        return ConcurrencyManager.stopped;
    }

    /**
     * Configure debugging: url query param and/or cookie to enable/disable debug logging
     */
    static configureDebug() {
        // check for URL query param to enable debugging
        const urlParams = new URLSearchParams(window.location.search);
        let debugConcurrency = urlParams.get(ConcurrencyManager.debugName);
        // check for persistent cookie to enable debugging
        if (!debugConcurrency) {
            debugConcurrency = getCookie(ConcurrencyManager.debugName);
        }
        // set/delete cookie
        if (ConcurrencyManager.debug) {
            setCookie(ConcurrencyManager.debugName, true, { path: '/' });
        } else {
            removeCookie(ConcurrencyManager.debugName);
        }
        // set debug mode from URL or cookie
        ConcurrencyManager.debug = debugConcurrency === 'true' || debugConcurrency === '1';
        // set shorter debug timer delay if enabled
        if (ConcurrencyManager.debug) {
            ConcurrencyManager.delay = ConcurrencyManager.debugDelay;
        }
        // check for force flag, e.g. force overthreshold for testing
        let debugConcurrencyForce = urlParams.get(ConcurrencyManager.debugNameForce);
        ConcurrencyManager.force =
            debugConcurrencyForce === 'true' || debugConcurrencyForce === '1';
    }

    /**
     * Start concurrency check timer if enababled
     * Called from SmartTag player event - we wait for content to start playing.
     *
     * @param eventType string
     * @return void
     */
    static playerStateChange(eventType) {
        ConcurrencyManager.debugLog('player state change:', eventType);
        ConcurrencyManager.debugLog('timerStarted: ', ConcurrencyManager.timerStarted);
        if (eventType === ConcurrencyManager.smartTagEvent) {
            if (
                !ConcurrencyManager.timerStarted &&
                ConcurrencyManager.timerContentId !== ConcurrencyManager.contentId
            ) {
                if (ConcurrencyManager.enabled) {
                    ConcurrencyManager.debugLog(
                        'starting timer for ' + ConcurrencyManager.delay + ' seconds',
                    );
                    ConcurrencyManager.timerStarted = true;
                    ConcurrencyManager.timerContentId = ConcurrencyManager.contentId;
                    // set timer
                    setTimeout(
                        ConcurrencyManager.processConcurrencyCheck,
                        ConcurrencyManager.delay * 1000,
                    );
                }
            }
        }
    }

    /**
     * Process the concurrency check using AJAX api call to endpoint
     * Called from timer
     */
    static processConcurrencyCheck() {
        let url = `${CBS.Registry.region.prefix}/video/concurrency.json?contentId=${ConcurrencyManager.contentId}`;
        let promise = new Promise((resolve, reject) => {
            fetch(url)
                .then((response) => {
                    return response.json();
                })
                .then((response) => {
                    if (!response?.success) {
                        reject();
                    }
                    ConcurrencyManager.debugLog(
                        'check after ' + ConcurrencyManager.delay + ' seconds',
                        response,
                    );
                    ConcurrencyManager.debugLog(
                        'concurrentStreamCount: ' + response?.result?.concurrentStreamCount,
                    );
                    let overThreshold = response?.result?.overThreshold;
                    ConcurrencyManager.debugLog('over threshold:', overThreshold);
                    if (overThreshold) {
                        ConcurrencyManager.debugLog(
                            'eligible for upsell: ' +
                                response.result?.deniedPlaybackReason?.eligibleForUpsell,
                        );
                        ConcurrencyManager.eligibleForUpsell =
                            response.result?.deniedPlaybackReason?.eligibleForUpsell;
                    }
                    if (response.result?.overThreshold || ConcurrencyManager.force) {
                        if (!response.result?.overThreshold && ConcurrencyManager.force) {
                            ConcurrencyManager.eligibleForUpsell = true;
                            ConcurrencyManager.debugLog('forced!');
                        }
                        ConcurrencyManager.overThreshold();
                    }
                    // return resolve();
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    /**
     * Process fact that user is over threshold
     * - stop SmartTag playback
     * - show concurrency slate
     */
    static overThreshold() {
        // stop SmartTag playback
        ConcurrencyManager.smartTag.API.VIDEO.interrupt({
            reason: 'VideoInterruptType.STOP_CONTENT_AFTER_AD',
            callback: () => {
                ConcurrencyManager.stopped = true;
                ConcurrencyManager.debugLog('interrupted video playback');
                // display the over threshold / concurrency slate
                ConcurrencyManager.displaySlate();
            },
        });
    }

    /**
     * Display concurrency slate
     * - hide player div
     * - show concurrency slate
     * - configure Redfast for INTL upsell prompt
     */
    static displaySlate() {
        // hide video player
        let element = document.querySelector('[data-ci=player-main]');
        if (element) {
            element.style.display = 'none';
        }
        // show concurrency slate
        element = document.querySelector('[data-ci=player-concurrency-slate]');
        if (element) {
            element.style.display = 'block';
            element.classList.remove('video__player-area-hidden');
            element.classList.add('video__player-area');
        }
        // configure Redfast for INTL if eligible
        ConcurrencyManager.confiureRedFast();
        // configure/fire tracking
        ConcurrencyManager.tracking();
        ConcurrencyManager.debugLog('loaded slate');
    }

    /**
     * Emit tracking event for concurrency display state
     * Configure tracking for CTA button click
     */
    static tracking() {
        let button = document.querySelector(
            '.video__lock-state-message-container.stream-exceed .button',
        );
        if (button) {
            // view action
            ConcurrencyManager.debugLog('tracking setup');
            let text =
                String(document.querySelector('.video__lock-slate-text').innerHTML.trim() ?? '') +
                String(
                    document.querySelector('.video__lock-slate-text-secondary').innerHTML.trim() ??
                        '',
                );
            let obj = {
                playerName: avia?.buildInfo.playerName ?? '',
                playerVersion: avia?.buildInfo.playerVersion ?? '',
                appLogText: text,
                appLogCode: 'MAX_STREAM_COUNT_EXCEEDED',
                appLogType: getLocale('error_too_many_streams'),
                contentType: utag_data.mediaContentType ?? '',
                mediaContentType: utag_data.mediaContentType ?? '',
            };
            ConcurrencyManager.debugLog('utag_data:', utag_data);
            ConcurrencyManager.debugLog('trackAction trackAppLog', obj);
            trackAction('trackAppLog', obj);

            // cta click action
            button.addEventListener('click', function (event) {
                obj.ctaText = button.innerText;
                ConcurrencyManager.debugLog('trackAction concurrencyPromptCta1', obj);
                trackAction('concurrencyPromptCta1', obj);
            });
        }
    }

    /**
     * Configure Redfast upsell popup
     * - to avoid premature Redfast popup on page load with hidden concurrency slate
     * we use a placeholder classname that Redfast does not know
     * - here, we set the correct classname and then "trigger" the Redfast service to rescan the DOM
     */
    static confiureRedFast() {
        ConcurrencyManager.debugLog('redfast eligible:', ConcurrencyManager.eligibleForUpsell);
        if (ConcurrencyManager.eligibleForUpsell) {
            // find the div and change class name so it is visible to Redfast
            var element = document.querySelector('.standard_upgrade_concurrency_hidden');
            ConcurrencyManager.debugLog('found redfast element', element);
            if (element) {
                /*
                 * See VideoBlock.php for depdencies on these class names
                 */
                ConcurrencyManager.debugLog(
                    'resetting Redfast class name',
                    'standard_upgrade_concurrency',
                );
                element.className = 'standard_upgrade_concurrency';
                element.style.display = 'block';

                ConcurrencyManager.debugLog('nudging Redfast Service');
                CBS.Registry.redfastService.attachEventListeners();
            } else {
                ConcurrencyManager.debugLog('redfast element not found');
            }
        }
    }

    /**
     * Simple debug console wrapper to check debug flag and prepend label
     */
    static debugLog(text, object = null) {
        if (ConcurrencyManager.debug) {
            text = 'concurrency: ' + text;
            object !== null ? console.log(text, object) : console.log(text);
        }
    }
}
